import AccessibleMenu from "accessible-menu";

const nav = document.querySelector(".js-main-nav");

if (nav) {
    const menuElement = nav.querySelector("ul.menu");
    const controllerElement = document.querySelector("button.menu-toggle");
    const navigationWrapper = nav.closest(".js-main-nav-wrapper");
    const openLayoutClass = "is-mobile-navigation-open";

    const menu = new AccessibleMenu.DisclosureMenu({
        menuElement,
        submenuItemSelector: "li.dropdown",
        submenuToggleSelector: ".menu-toggle-link",
        containerElement: nav,
        controllerElement,
        optionalKeySupport: true,
        hoverType: "off",
        transitionClass: null,
    });

    const mqDesktop = window.matchMedia("(min-width: 1024px)");

    const syncNavigationLayout = () => {
        if (!controllerElement) {
            return;
        }

        const isOpen = controllerElement.getAttribute("aria-expanded") === "true";

        if (navigationWrapper) {
            navigationWrapper.classList.toggle(openLayoutClass, isOpen);
        }
    };

    const resetMenuForDesktop = (e) => {
        const isDesktop = e.matches ?? e;

        if (!isDesktop) {
            return;
        }

        const mainMenu = menuElement;

        if (!mainMenu) {
            return;
        }

        mainMenu.classList.remove("hide", "show", "transitioning");
        mainMenu.style.display = "";

        mainMenu.querySelectorAll(".dropdown-menu").forEach((submenu) => {
            submenu.classList.remove("hide", "show", "transitioning");
            submenu.style.display = "";
        });

        nav.querySelectorAll("[aria-expanded='true']").forEach((el) => {
            el.setAttribute("aria-expanded", "false");
        });
    };

    resetMenuForDesktop(mqDesktop);
    mqDesktop.addEventListener("change", resetMenuForDesktop);

    const updateSubmenuDirections = () => {
        const dropdownItems = nav.querySelectorAll("li.dropdown");

        dropdownItems.forEach((item) => {
            const submenu = item.querySelector(":scope > .dropdown-menu");

            if (!submenu) {
                return;
            }

            item.classList.remove("dropdown-left");

            if (!mqDesktop.matches) {
                return;
            }

            const hadHideClass = submenu.classList.contains("hide");
            const hadHiddenClass = submenu.classList.contains("hidden");

            if (hadHideClass) {
                submenu.classList.remove("hide");
            }

            if (hadHiddenClass) {
                submenu.classList.remove("hidden");
            }

            const prevDisplay = submenu.style.display;
            const prevVisibility = submenu.style.visibility;

            submenu.style.visibility = "hidden";
            submenu.style.display = "block";

            const rect = submenu.getBoundingClientRect();

            submenu.style.display = prevDisplay;
            submenu.style.visibility = prevVisibility;

            if (hadHideClass) {
                submenu.classList.add("hide");
            }

            if (hadHiddenClass) {
                submenu.classList.add("hidden");
            }

            const viewportWidth =
                document.documentElement.clientWidth || window.innerWidth;

            if (rect.right > viewportWidth) {
                item.classList.add("dropdown-left");
            }
        });
    };

    const getFocusableSubmenuItems = (submenu) => {
        if (!submenu) {
            return [];
        }

        return Array.from(
            submenu.querySelectorAll(
                'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )
        ).filter((el) => {
            return !el.hasAttribute("disabled") && el.offsetParent !== null;
        });
    };

    const focusFirstItemInSubmenu = (toggle) => {
        if (!toggle) {
            return;
        }

        const parentItem = toggle.closest("li.dropdown");

        if (!parentItem) {
            return;
        }

        const submenu = parentItem.querySelector(":scope > .dropdown-menu");

        if (!submenu) {
            return;
        }

        const items = getFocusableSubmenuItems(submenu);

        if (items.length) {
            items[0].focus();
        }
    };

    const focusFirstItemInSubmenuAfterOpen = (toggle) => {
        if (!toggle) {
            return;
        }

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (toggle.getAttribute("aria-expanded") !== "true") {
                    return;
                }

                focusFirstItemInSubmenu(toggle);
            });
        });
    };

    const closeSubmenuAndReturnFocus = (submenu) => {
        const parentDropdown = submenu.closest("li.dropdown");

        if (!parentDropdown) {
            return;
        }

        const parentToggle = parentDropdown.querySelector(":scope > .menu-toggle-link");

        if (!parentToggle) {
            return;
        }

        if (parentToggle.getAttribute("aria-expanded") === "true") {
            parentToggle.click();
        }

        parentToggle.focus();
    };

    const handleSubmenuArrowKeys = (event) => {
        const key = event.key;

        const currentItem = event.target.closest(
            '.dropdown-menu a[href], .dropdown-menu button:not([disabled]), .dropdown-menu [tabindex]:not([tabindex="-1"])'
        );

        if (!currentItem) {
            return;
        }

        const submenu = currentItem.closest(".dropdown-menu");

        if (!submenu) {
            return;
        }

        const items = getFocusableSubmenuItems(submenu);
        const currentIndex = items.indexOf(currentItem);

        if (currentIndex === -1 || items.length === 0) {
            return;
        }

        switch (key) {
            case "ArrowDown":
            case "Down":
                event.preventDefault();
                items[(currentIndex + 1) % items.length].focus();
                break;

            case "ArrowUp":
            case "Up":
                event.preventDefault();
                items[(currentIndex - 1 + items.length) % items.length].focus();
                break;

            case "Home":
                event.preventDefault();
                items[0].focus();
                break;

            case "End":
                event.preventDefault();
                items[items.length - 1].focus();
                break;

            case "Escape":
            case "Esc":
                event.preventDefault();
                closeSubmenuAndReturnFocus(submenu);
                break;

            default:
                break;
        }
    };

    const observeSubmenuToggleFocus = (toggle) => {
        if (!toggle) {
            return;
        }

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (
                    mutation.type === "attributes" &&
                    mutation.attributeName === "aria-expanded" &&
                    toggle.getAttribute("aria-expanded") === "true"
                ) {
                    updateSubmenuDirections();
                    focusFirstItemInSubmenuAfterOpen(toggle);
                }
            });
        });

        observer.observe(toggle, {
            attributes: true,
            attributeFilter: ["aria-expanded"],
        });
    };

    updateSubmenuDirections();

    window.addEventListener("load", () => {
        updateSubmenuDirections();
    });

    if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
            updateSubmenuDirections();
        });
    }

    window.addEventListener("resize", () => {
        updateSubmenuDirections();
    });

    if (controllerElement) {
        const controllerObserver = new MutationObserver(syncNavigationLayout);

        controllerObserver.observe(controllerElement, {
            attributes: true,
            attributeFilter: ["aria-expanded"],
        });

        syncNavigationLayout();

        controllerElement.addEventListener("click", () => {
            requestAnimationFrame(() => {
                updateSubmenuDirections();
            });
        });
    }

    const submenuToggles = nav.querySelectorAll(".menu-toggle-link");

    submenuToggles.forEach((toggle) => {
        observeSubmenuToggleFocus(toggle);

        // Critical: prevent href jump behavior before the accessible-menu handler runs.
        toggle.addEventListener(
            "click",
            (event) => {
                event.preventDefault();
            },
            true
        );

        toggle.addEventListener(
            "keydown",
            (event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                }
            },
            true
        );

        toggle.addEventListener("click", () => {
            requestAnimationFrame(() => {
                updateSubmenuDirections();
                focusFirstItemInSubmenuAfterOpen(toggle);
            });
        });

        toggle.addEventListener("keydown", (event) => {
            if (event.key !== "Enter" && event.key !== " ") {
                return;
            }

            requestAnimationFrame(() => {
                updateSubmenuDirections();
                focusFirstItemInSubmenuAfterOpen(toggle);
            });
        });
    });

    nav.addEventListener("keydown", handleSubmenuArrowKeys);

    if (controllerElement) {
        controllerElement.disabled = false;
    }
}
